<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">APPROACH</div>
      <textarea
        class="drawer-fieldTextarea"
        v-model="localSenderApproach"
        placeholder="How should this sender write and follow up?"
        rows="3"
        @blur="applySenderApproach"
      />
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">SEQUENCE</div>
      <div v-if="stepCount > 0" class="sequenceSummary">
        <span class="stepCount">{{ stepCount }} step{{ stepCount !== 1 ? 's' : '' }}</span>
        <button class="editLink" type="button" @click="openEditor">Edit steps</button>
      </div>
      <div v-else class="emptySummary">
        <span class="mutedText">No sequence defined yet.</span>
        <button class="btn secondary" type="button" @click="openEditor">Create sequence</button>
      </div>
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">SCHEDULING</div>
      <label class="customizeLocallyLabel">
        <input type="checkbox" v-model="localSchedulingCustomized" @change="applyScheduling" />
        Customize scheduling for this play
      </label>
      <SchedulingSettingsFields
        v-if="localSchedulingCustomized"
        v-model:timezone-aware-sending="localSchedulingTimezoneAware"
        v-model:default-us-timezone="localSchedulingDefaultUsTimezone"
        v-model:default-send-timezone="localSchedulingDefaultSendTimezone"
        @update:timezone-aware-sending="applyScheduling"
        @update:default-us-timezone="applyScheduling"
        @update:default-send-timezone="applyScheduling"
      />
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">SAMPLE EMAILS</div>
      <div v-if="localSampleEmails.length === 0" class="mutedText">
        No sample emails yet. Add one to guide the agent's writing style.
      </div>
      <div v-else class="emailList">
        <div v-for="(email, i) in localSampleEmails" :key="email.id || i" class="emailRow">
          <div class="emailPreview">{{ truncate(email.body, 120) }}</div>
          <div class="emailActions">
            <button class="emailActionBtn" type="button" @click="editSampleEmail(i)">Edit</button>
            <button class="emailActionBtn emailActionBtn--danger" type="button" @click="deleteSampleEmail(i)">Delete</button>
          </div>
        </div>
      </div>
      <button class="addLink" type="button" @click="addSampleEmail">+ Add sample email</button>
    </div>
  </SalesPlayDrawerCard>

  <!-- Teleported: the context drawer's sliding pane is a `transform` +
       `will-change: transform` container, which becomes the containing
       block for a fixed-position modal overlay — without this the modal
       renders pinned inside the drawer's own (narrow, short) box instead
       of centered over the full viewport. -->
  <Teleport to="body">
    <SequenceEditorModal
      v-if="editorOpen"
      :sequence-id="localSequenceId"
      :initial-name="localSequenceName"
      :initial-description="localSequenceDescription"
      :initial-steps="localSteps"
      :step-types="stepTypes"
      @close="editorOpen = false"
      @saved="onSequenceSaved"
    />
  </Teleport>

  <Teleport to="body">
    <Modal
      v-if="emailModalOpen"
      :title="editingEmailIndex !== null ? 'Edit Sample Email' : 'Add Sample Email'"
      :max-width="600"
      @close="emailModalOpen = false"
    >
      <textarea
        class="emailTextarea"
        v-model="emailDraft"
        placeholder="Paste or write a sample email that represents the tone and style you want the agent to use..."
        rows="10"
      />
      <template #footer>
        <button class="btn secondary" @click="emailModalOpen = false">Cancel</button>
        <button class="btn primary" :disabled="!emailDraft.trim()" @click="saveSampleEmail">
          {{ editingEmailIndex !== null ? 'Save' : 'Add' }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import SequenceEditorModal from './SequenceEditorModal.vue'
import Modal from '../../components/Modal.vue'
import SchedulingSettingsFields from '../../components/SchedulingSettingsFields.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'

interface SampleEmail {
  id?: string
  body: string
}

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
}>()

const { nodes, updateNodeConfig, stepTypes } = useSalesPlayDraft()

// ── Local working copy ──────────────────────────────────────────

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localSenderApproach = ref(currentConfig().sender_approach || '')
const localSequenceId = ref(currentConfig().sequence_id || '')
const localSequenceName = ref(currentConfig().name || '')
const localSequenceDescription = ref(currentConfig().sequence_description || '')
const localSteps = ref<any[]>(currentConfig().steps || [])
const localSampleEmails = ref<SampleEmail[]>(
  (currentConfig().sample_emails || []).map((e: any) => ({ ...e }))
)
const localSchedulingCustomized = ref(!!currentConfig().scheduling_customized)
const localSchedulingTimezoneAware = ref(!!currentConfig().scheduling_timezone_aware_sending)
const localSchedulingDefaultUsTimezone = ref(currentConfig().scheduling_default_us_timezone || '')
const localSchedulingDefaultSendTimezone = ref(currentConfig().scheduling_default_send_timezone || '')

const stepCount = computed(() => localSteps.value.filter((s: any) => s.step_type !== 'wait').length)

function resetLocal() {
  const cfg = currentConfig()
  localSenderApproach.value = cfg.sender_approach || ''
  localSequenceId.value = cfg.sequence_id || ''
  localSequenceName.value = cfg.name || ''
  localSequenceDescription.value = cfg.sequence_description || ''
  localSteps.value = cfg.steps || []
  localSampleEmails.value = (cfg.sample_emails || []).map((e: any) => ({ ...e }))
  localSchedulingCustomized.value = !!cfg.scheduling_customized
  localSchedulingTimezoneAware.value = !!cfg.scheduling_timezone_aware_sending
  localSchedulingDefaultUsTimezone.value = cfg.scheduling_default_us_timezone || ''
  localSchedulingDefaultSendTimezone.value = cfg.scheduling_default_send_timezone || ''
}

watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// ── Sequence editor modal ───────────────────────────────────────

const editorOpen = ref(false)

function openEditor() {
  editorOpen.value = true
}

function onSequenceSaved(result: any) {
  localSequenceId.value = result?.id || localSequenceId.value
  localSequenceName.value = result?.name || localSequenceName.value
  localSequenceDescription.value = result?.description || ''
  localSteps.value = (result?.steps || []).map((s: any) => ({
    step_index: s.step_index,
    step_type: s.step_type,
    brief: s.brief || '',
    wait_days: s.wait_days || null,
    wait_unit: s.wait_unit || 'days',
  }))
  updateNodeConfig(props.nodeIndex, {
    sequence_id: localSequenceId.value,
    name: localSequenceName.value,
    sequence_description: localSequenceDescription.value,
    steps: localSteps.value,
  })
  editorOpen.value = false
}

// ── Sample email modal ──────────────────────────────────────────

const emailModalOpen = ref(false)
const emailDraft = ref('')
const emailError = ref('')
const editingEmailIndex = ref<number | null>(null)

function addSampleEmail() {
  editingEmailIndex.value = null
  emailDraft.value = ''
  emailError.value = ''
  emailModalOpen.value = true
}

function editSampleEmail(index: number) {
  editingEmailIndex.value = index
  emailDraft.value = localSampleEmails.value[index]?.body || ''
  emailError.value = ''
  emailModalOpen.value = true
}

function saveSampleEmail() {
  const body = emailDraft.value.trim()
  if (!body) return

  if (editingEmailIndex.value !== null) {
    const existing = localSampleEmails.value[editingEmailIndex.value]
    localSampleEmails.value[editingEmailIndex.value] = { ...existing, body }
  } else {
    localSampleEmails.value.push({ body })
  }
  updateNodeConfig(props.nodeIndex, { sample_emails: localSampleEmails.value })
  emailModalOpen.value = false
}

function deleteSampleEmail(index: number) {
  if (!confirm('Delete this sample email?')) return
  localSampleEmails.value.splice(index, 1)
  updateNodeConfig(props.nodeIndex, { sample_emails: localSampleEmails.value })
}

function truncate(text: string, max: number): string {
  if (!text) return ''
  return text.length > max ? text.slice(0, max) + '…' : text
}

function applySenderApproach() {
  updateNodeConfig(props.nodeIndex, { sender_approach: localSenderApproach.value })
}

async function applyScheduling() {
  if (localSchedulingCustomized.value && !currentConfig().scheduling_customized) {
    try {
      const globalSettings: any = await apiFetch('/settings/email')
      localSchedulingTimezoneAware.value = !!globalSettings.timezone_aware_sending
      localSchedulingDefaultUsTimezone.value = globalSettings.default_us_timezone || ''
      localSchedulingDefaultSendTimezone.value = globalSettings.default_send_timezone || ''
    } catch { /* pre-seeding is best-effort */ }
  }
  updateNodeConfig(props.nodeIndex, {
    scheduling_customized: localSchedulingCustomized.value,
    scheduling_timezone_aware_sending: localSchedulingTimezoneAware.value,
    scheduling_default_us_timezone: localSchedulingDefaultUsTimezone.value,
    scheduling_default_send_timezone: localSchedulingDefaultSendTimezone.value,
  })
}
</script>

<style scoped>
.sequenceSummary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.stepCount {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.editLink {
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
}
.editLink:hover { color: var(--color-text-primary); }

.emptySummary {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mutedText {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
}

.customizeLocallyLabel {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-secondary);
  cursor: pointer;
  margin-bottom: var(--space-3);
}

/* Sample emails */
.emailList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}
.emailRow {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
}
.emailPreview {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  line-height: var(--lh-normal);
  margin-bottom: var(--space-2);
  white-space: pre-wrap;
}
.emailActions {
  display: flex;
  gap: var(--space-2);
}
.emailActionBtn {
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
}
.emailActionBtn:hover { color: var(--color-text-primary); }
.emailActionBtn--danger { color: var(--color-text-tertiary); }
.emailActionBtn--danger:hover { color: var(--color-danger); }

.addLink {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) 0;
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
}
.addLink:hover { color: var(--color-text-primary); }

/* Email modal textarea */
.emailTextarea {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-relaxed, 1.6);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
  resize: vertical;
}
.emailTextarea:focus {
  outline: none;
  border-color: var(--color-accent);
}
.errorText {
  margin-top: var(--space-2);
  font-size: var(--fs-sm);
  color: var(--color-danger, #b91c1c);
}
</style>
